import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Sparkles } from "lucide-react";
import { useCart } from "../context/CartContext";

const CATEGORY_LABELS = {
  salgados: "Salgados",
  brigadeiros: "Brigadeiros",
  personalizados: "Personalizado",
  kits: "Kit pronto",
};

export default function ProductCard({ product, index = 0 }) {
  const { addItem } = useCart();
  const [selectedVariantId, setSelectedVariantId] = useState(product.variants[0].id);
  const variant = product.variants.find((v) => v.id === selectedVariantId);
  const hasFlavorChoice = (product.flavor_groups || []).length > 0;
  const requiresConfig = hasFlavorChoice || product.requires_customization;
  const categoryLabel = CATEGORY_LABELS[product.category] || "";

  const handleQuickAdd = (e) => {
    e.preventDefault();
    if (hasFlavorChoice) return; // shouldn't happen; UI will route to detail
    addItem(product, variant, 1, {});
  };

  return (
    <div
      data-testid={`product-card-${product.id}`}
      className="group bg-cream border border-cocoa/10 rounded-2xl overflow-hidden lift animate-fade-up flex flex-col"
      style={{ animationDelay: `${index * 60}ms` }}
    >
      <Link to={`/produto/${product.id}`} className="block relative aspect-square overflow-hidden bg-cream-alt">
        <img
          src={product.image_url}
          alt={product.name}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          loading="lazy"
        />
        <span className="absolute top-3 left-3 bg-cream/90 backdrop-blur px-3 py-1 rounded-full text-[10px] uppercase tracking-widest text-cocoa-dark">
          {categoryLabel}
        </span>
      </Link>
      <div className="p-5 flex flex-col flex-1">
        <Link to={`/produto/${product.id}`}>
          <h3 className="font-heading text-2xl text-cocoa-dark leading-tight">{product.name}</h3>
        </Link>
        <p className="text-sm text-cocoa-soft mt-1.5 line-clamp-2 min-h-[2.5rem]">{product.description}</p>

        {product.variants.length > 1 && (
          <div className="mt-4 flex flex-wrap gap-1.5">
            {product.variants.map((v) => (
              <button
                key={v.id}
                data-testid={`variant-pill-${product.id}-${v.id}`}
                onClick={(e) => { e.preventDefault(); setSelectedVariantId(v.id); }}
                className={`text-[11px] uppercase tracking-wider px-3 py-1.5 rounded-full border transition-colors ${
                  selectedVariantId === v.id
                    ? "bg-cocoa-dark text-cream border-cocoa-dark"
                    : "bg-cream-alt text-cocoa-soft border-cocoa/15 hover:border-rose"
                }`}
              >
                {v.label}
              </button>
            ))}
          </div>
        )}

        <div className="mt-5 flex items-center justify-between gap-3">
          <div>
            <div className="font-heading text-3xl text-cocoa-dark leading-none">€{variant.price.toFixed(2)}</div>
            <div className="text-[10px] uppercase tracking-widest text-rose-accent mt-1">
              {variant.pack_size} unidades
            </div>
          </div>
          {requiresConfig ? (
            <Link
              to={`/produto/${product.id}`}
              data-testid={`choose-flavors-${product.id}`}
              className="flex items-center gap-1.5 bg-rose hover:bg-rose-hover text-cocoa-dark px-4 py-2.5 rounded-full text-sm font-medium transition-colors"
            >
              <Sparkles className="w-4 h-4" /> {product.requires_customization ? "Personalizar" : "Escolher sabores"}
            </Link>
          ) : (
            <button
              data-testid={`add-to-cart-${product.id}`}
              onClick={handleQuickAdd}
              className="flex items-center gap-1.5 bg-rose hover:bg-rose-hover text-cocoa-dark px-4 py-2.5 rounded-full text-sm font-medium transition-colors"
            >
              <Plus className="w-4 h-4" /> Adicionar
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
